import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import HistoryService from "../../services/HistoryService";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import DataTableComponent from "react-data-table-component";

const WalletTab = () => {
  const today = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [searchText, setSearchText] = useState("");
  const [isInitState, setIsInitState] = useState(true);
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, searchText, page, size]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await HistoryService.wallet(
        fromDate,
        toDate,
        searchText,
        isInitState,
        size,
        page
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        setData(responseData.data.result);
        setTotalRows(responseData.data.total_count || 0);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const getAmountDisplay = (txn) => {
    const amount = parseFloat(txn.amount || 0).toFixed(2);
    const isCredit = txn.txn_type === "CR";
    const sign = isCredit ? "+" : "-";
    const color = isCredit ? "text-success" : "text-danger";
    return (
      <span className={color}>
        {sign} ₹{amount}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "SUCCESS":
        return <span className="badge bg-success">{status}</span>;
      case "FAILED":
      case "REFUND":
        return <span className="badge bg-danger">{status}</span>;
      case "PENDING":
        return <span className="badge bg-warning">{status}</span>;
      default:
        return <span className="badge bg-secondary">{status || "-"}</span>;
    }
  };

  // Custom cell renderer for all columns
  const renderCell = (value) => (
    <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {value || "-"}
    </div>
  );

  const columns = [
    {
      name: "#",
      selector: (row, index) => (page - 1) * size + index + 1,
      cell: (row, index) => renderCell((page - 1) * size + index + 1),
      width: "60px",
    },
    {
      name: "Date",
      selector: (row) => row.created_on,
      cell: (row) => renderCell(row.created_on),
      sortable: true,
    },
    {
      name: "Product/Service",
      selector: (row) =>
        `${row.product?.sub_master_product?.name || ""} | ${
          row.product?.name || ""
        }`,
      cell: (row) =>
        renderCell(
          `${row.product?.sub_master_product?.name || ""} | ${
            row.product?.name || ""
          }`
        ),
    },
    {
      name: "Order ID",
      selector: (row) => row.order_id,
      cell: (row) => renderCell(row.order_id),
    },
    {
      name: "Order Amount",
      selector: (row) => `₹${row.order_amount || 0}`,
      cell: (row) => renderCell(`₹${row.order_amount || 0}`),
    },
    {
      name: "Amount",
      cell: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {getAmountDisplay(row)}
        </div>
      ),
    },
    {
      name: "Balance",
      selector: (row) => `₹${row.closing_bal || "-"}`,
      cell: (row) => renderCell(`₹${row.closing_bal || "-"}`),
    },
    {
      name: "Status",
      cell: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {getStatusBadge(row.status)}
        </div>
      ),
    },
  ];

  const customStyles = {
    cells: {
      style: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        padding: "8px",
      },
    },
    headCells: {
      style: {
        padding: "8px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        textAlign: "left",
      },
    },
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setSize(newPerPage);
    setPage(page);
  };

  return (
    <div>
      {loading && <LoadingScreen />}

      {/* Toggle Filter Button */}
      <div className="mb-3 text-end">
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="ps-3 pe-3">
          <div className="row mb-4 border p-3 rounded bg-light">
            <div className="col-md-3">
              <label>From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setIsInitState(false);
                }}
              />
            </div>
            <div className="col-md-3">
              <label>To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setIsInitState(false);
                }}
              />
            </div>
            <div className="col-md-4">
              <label>Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by reference or remarks"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-primary w-100" onClick={fetchData}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTableComponent
        columns={columns}
        data={data}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={size}
        paginationDefaultPage={page}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        highlightOnHover
        striped
        persistTableHead
        responsive
        noDataComponent="No records found"
        customStyles={customStyles}
      />
    </div>
  );
};

export default WalletTab;
